import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { PageHeader } from '@/components/PageHeader';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Surface } from '@/components/ui/Surface';
import { resolveTusEndpoint, UPLOAD_TOKEN_HEADER } from '@/lib/env';
import { describeError } from '@/lib/http';
import { formatFileSize } from '@/lib/format';
import { zodResolver } from '@/lib/zodResolver';
import { useCurrentUserQuery } from '@/features/user/queries';

import {
  confirmUploadComplete,
  createTranscription,
  createUploadSession,
  fetchClientSuggestions,
  fetchDirectoryUsers,
} from '../api';
import {
  MAX_FILE_SIZE_BYTES,
  MAX_PARTS_COUNT,
  MAX_TOTAL_SIZE_BYTES,
  UploadDropzone,
} from '../components/UploadDropzone';
import { ClientNameCombobox } from '../components/ClientNameCombobox';
import { MeetingDatePicker } from '../components/MeetingDatePicker';
import { ParticipantChips } from '../components/ParticipantChips';
import { MEETING_TYPE_OPTIONS, todayMeetingDate, type MeetingType } from '../meetingLabels';

const CHUNK_SIZE = 16 * 1024 * 1024;
const RETRY_DELAYS = [0, 1000, 3000, 5000];

const formSchema = z
  .object({
    title: z.string().trim().min(1, 'Укажите название встречи').max(500),
    meetingType: z.enum(['PRE_SALE', 'CLIENT_WORK', 'INTERNAL']),
    meetingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'Укажите дату'),
    clientName: z.string().trim().max(300),
  })
  .superRefine((value, ctx) => {
    if (
      (value.meetingType === 'PRE_SALE' || value.meetingType === 'CLIENT_WORK') &&
      value.clientName.trim() === ''
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите наименование клиента',
        path: ['clientName'],
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

type PartUploadProgress = {
  partIndex: number;
  fileName: string;
  bytesUploaded: number;
  bytesTotal: number;
  percentage: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
};

type UploadProgress = {
  parts: PartUploadProgress[];
  bytesPerSecond: number;
};

function titleFromFileName(fileName: string): string {
  const base = fileName.replace(/^.*[/\\]/u, '');
  const withoutExt = base.replace(/\.[^.]+$/u, '');
  return withoutExt.trim() || base.trim() || 'Встреча';
}

function formatSpeed(bytesPerSecond: number): string {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) {
    return '—';
  }
  return `${formatFileSize(String(Math.round(bytesPerSecond)))}/с`;
}

function formatEta(bytesUploaded: number, bytesTotal: number, bytesPerSecond: number): string {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0 || bytesTotal <= bytesUploaded) {
    return '—';
  }
  const secondsLeft = Math.ceil((bytesTotal - bytesUploaded) / bytesPerSecond);
  if (secondsLeft < 60) {
    return `≈ ${secondsLeft} с`;
  }
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  return `≈ ${minutes} мин ${seconds.toString().padStart(2, '0')} с`;
}

export function NewTranscriptionPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [axiumParticipantIds, setAxiumParticipantIds] = useState<string[]>([]);
  const [clientParticipants, setClientParticipants] = useState<string[]>([]);
  const [clientParticipantDraft, setClientParticipantDraft] = useState('');
  const [axiumSearch, setAxiumSearch] = useState('');
  const titleEditedRef = useRef(false);
  const transcriptionIdRef = useRef<string | null>(null);
  const uploadSessionIdsRef = useRef<string[]>([]);
  const uppiesRef = useRef<Uppy[]>([]);
  const progressStartedAtRef = useRef<number | null>(null);

  const meQuery = useCurrentUserQuery();
  const usersQuery = useQuery({
    queryKey: ['directory-users'],
    queryFn: ({ signal }) => fetchDirectoryUsers(signal),
    staleTime: 60_000,
  });
  const clientSuggestionsQuery = useQuery({
    queryKey: ['client-suggestions'],
    queryFn: ({ signal }) => fetchClientSuggestions(undefined, signal),
    staleTime: 60_000,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      meetingType: 'PRE_SALE',
      meetingDate: todayMeetingDate(),
      clientName: '',
    },
    mode: 'onSubmit',
  });

  const meetingType = watch('meetingType') as MeetingType;
  const clientName = watch('clientName');
  const showClientFields = meetingType !== 'INTERNAL';

  useEffect(() => {
    return () => {
      uppiesRef.current.forEach((uppy) => uppy?.destroy());
      uppiesRef.current = [];
    };
  }, []);

  const directoryUsers = usersQuery.data ?? [];
  const filteredUsers = useMemo(() => {
    const q = axiumSearch.trim().toLowerCase();
    return directoryUsers.filter((user) => {
      if (axiumParticipantIds.includes(user.id)) {
        return false;
      }
      if (q === '') {
        return true;
      }
      return (
        user.displayName.toLowerCase().includes(q) || user.email.toLowerCase().includes(q)
      );
    });
  }, [axiumSearch, axiumParticipantIds, directoryUsers]);

  const selectedAxium = directoryUsers.filter((user) => axiumParticipantIds.includes(user.id));

  const onFilesChange = (nextFiles: File[]) => {
    setFiles(nextFiles);
    setFileError(null);
    setSubmitError(null);
    setProgress(null);
    setPaused(false);
    uploadSessionIdsRef.current = [];
    uppiesRef.current.forEach((uppy) => uppy?.cancelAll());
    uppiesRef.current.forEach((uppy) => uppy?.destroy());
    uppiesRef.current = [];

    if (nextFiles.length === 0) {
      if (!titleEditedRef.current) {
        setValue('title', '');
      }
      return;
    }

    // Validate file sizes
    const invalidFiles = nextFiles.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (invalidFiles.length > 0) {
      setFileError(
        `Файл${invalidFiles.length > 1 ? 'ы' : ''} больше 3 ГБ: ${invalidFiles.map((f) => f.name).join(', ')}. Выберите файлы меньшего размера.`,
      );
    }

    // Validate total size for multipart
    if (nextFiles.length > 1) {
      const totalSize = nextFiles.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE_BYTES) {
        setFileError(
          `Суммарный размер ${formatFileSize(String(totalSize))} превышает лимит ${formatFileSize(String(MAX_TOTAL_SIZE_BYTES))}. Уменьшите количество или размер файлов.`,
        );
      }
    }

    // Validate parts count
    if (nextFiles.length > MAX_PARTS_COUNT) {
      setFileError(`Максимум ${MAX_PARTS_COUNT} частей. Вы выбрали ${nextFiles.length}.`);
    }

    // Set title from first file if not edited
    if (!titleEditedRef.current && nextFiles.length > 0) {
      const firstFile = nextFiles[0];
      if (firstFile) {
        setValue('title', titleFromFileName(firstFile.name), { shouldValidate: true });
      }
    }
  };

  const destroyUppies = () => {
    uppiesRef.current.forEach((uppy) => uppy?.destroy());
    uppiesRef.current = [];
  };

  const addClientParticipant = () => {
    const name = clientParticipantDraft.trim();
    if (name === '') {
      return;
    }
    setClientParticipants((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setClientParticipantDraft('');
  };

  const runUpload = handleSubmit(async (values) => {
    if (files.length === 0) {
      setFileError('Выберите файл или файлы для загрузки');
      return;
    }

    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    const isMultipart = files.length > 1;

    // Validate file sizes
    const invalidFiles = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (invalidFiles.length > 0) {
      setFileError(
        `Файл${invalidFiles.length > 1 ? 'ы' : ''} больше 3 ГБ: ${invalidFiles.map((f) => f.name).join(', ')}`,
      );
      return;
    }

    if (isMultipart && totalSize > MAX_TOTAL_SIZE_BYTES) {
      setFileError(`Суммарный размер превышает ${formatFileSize(String(MAX_TOTAL_SIZE_BYTES))}`);
      return;
    }

    setSubmitError(null);
    setUploading(true);
    setPaused(false);

    const initialProgress: PartUploadProgress[] = files.map((file, index) => ({
      partIndex: index,
      fileName: file.name,
      bytesUploaded: 0,
      bytesTotal: file.size,
      percentage: 0,
      status: 'pending',
    }));

    setProgress({
      parts: initialProgress,
      bytesPerSecond: 0,
    });
    progressStartedAtRef.current = Date.now();

    const isInternal = values.meetingType === 'INTERNAL';

    try {
      let transcriptionId = transcriptionIdRef.current;
      if (transcriptionId === null) {
        const firstFile = files[0];
        if (!firstFile) {
          throw new Error('Первый файл не найден');
        }
        const created = await createTranscription({
          title: values.title.trim() || titleFromFileName(firstFile.name),
          originalFileName: firstFile.name,
          sourceMimeType: firstFile.type || 'application/octet-stream',
          sourceFileSize: String(firstFile.size),
          meetingType: values.meetingType,
          meetingDate: values.meetingDate,
          clientName: isInternal ? null : values.clientName.trim(),
          axiumParticipantIds,
          clientParticipantNames: isInternal ? [] : clientParticipants,
        });
        transcriptionId = created.id;
        transcriptionIdRef.current = transcriptionId;
      }

      // Create upload sessions for all parts
      const sessions = await Promise.all(
        files.map((file, index) =>
          createUploadSession(transcriptionId!, {
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
            ...(isMultipart
              ? {
                  partIndex: index,
                  expectedPartsCount: files.length,
                }
              : {}),
          }),
        ),
      );
      uploadSessionIdsRef.current = sessions.map((s) => s.uploadSessionId);

      destroyUppies();

      // Create Uppy instances for each part
      const uppies = files.map((file, index) => {
        const session = sessions[index];
        if (!session) {
          throw new Error(`Сессия для части ${index} не создана`);
        }
        const uppy = new Uppy({
          id: `axium-upload-${transcriptionId}-part-${index}`,
          autoProceed: false,
          restrictions: {
            maxNumberOfFiles: 1,
            maxFileSize: MAX_FILE_SIZE_BYTES,
          },
        });

        uppy.use(Tus, {
          endpoint: resolveTusEndpoint(session.tusEndpoint),
          headers: {
            [UPLOAD_TOKEN_HEADER]: session.uploadToken,
          },
          chunkSize: CHUNK_SIZE,
          retryDelays: RETRY_DELAYS,
          limit: 1,
          storeFingerprintForResuming: true,
          removeFingerprintOnSuccess: true,
          allowedMetaFields: [
            'uploadSessionId',
            'transcriptionId',
            'filename',
            'partIndex',
            'expectedPartsCount',
          ],
        });

        uppy.on('upload-progress', (_file, progressEvent) => {
          const bytesUploaded = progressEvent.bytesUploaded ?? 0;
          const bytesTotal = progressEvent.bytesTotal ?? file.size;
          const percentage =
            bytesTotal > 0 ? Math.min(100, Math.round((bytesUploaded / bytesTotal) * 100)) : 0;

          setProgress((prev) => {
            if (!prev) {
              return prev;
            }
            const newParts = [...prev.parts];
            const currentPart = newParts[index];
            if (!currentPart) {
              return prev;
            }
            newParts[index] = {
              partIndex: currentPart.partIndex,
              fileName: currentPart.fileName,
              bytesUploaded,
              bytesTotal,
              percentage,
              status: bytesUploaded === bytesTotal ? 'completed' : 'uploading',
            };

            const totalUploaded = newParts.reduce((sum, p) => sum + p.bytesUploaded, 0);
            const startedAt = progressStartedAtRef.current ?? Date.now();
            const elapsedSec = Math.max((Date.now() - startedAt) / 1000, 0.001);
            const bytesPerSecond = totalUploaded / elapsedSec;

            return { parts: newParts, bytesPerSecond };
          });
        });

        uppy.addFile({
          name: file.name,
          type: file.type || 'application/octet-stream',
          data: file,
          source: 'local',
          meta: {
            uploadSessionId: session.uploadSessionId,
            transcriptionId,
            filename: file.name,
            ...(isMultipart
              ? {
                  partIndex: String(index),
                  expectedPartsCount: String(files.length),
                }
              : {}),
          },
        });

        return uppy;
      });

      uppiesRef.current = uppies;

      // Start all uploads in parallel
      const uploadPromises = uppies.map(
        (uppy, index) =>
          new Promise<{ uploadUrl?: string }>((resolve, reject) => {
            uppy.on('upload-success', (uploadedFile) => {
              const uploadUrl =
                typeof uploadedFile?.uploadURL === 'string' ? uploadedFile.uploadURL : undefined;
              setProgress((prev) => {
                if (!prev) {
                  return prev;
                }
                const newParts = [...prev.parts];
                const currentPart = newParts[index];
                if (!currentPart) {
                  return prev;
                }
                newParts[index] = {
                  ...currentPart,
                  status: 'completed',
                };
                return { ...prev, parts: newParts };
              });
              if (uploadUrl) {
                resolve({ uploadUrl });
              } else {
                resolve({});
              }
            });

            uppy.on('upload-error', (_uploadedFile, error) => {
              const errorMsg = error instanceof Error ? error.message : String(error);
              setProgress((prev) => {
                if (!prev) {
                  return prev;
                }
                const newParts = [...prev.parts];
                const currentPart = newParts[index];
                if (!currentPart) {
                  return prev;
                }
                newParts[index] = {
                  ...currentPart,
                  status: 'error',
                  error: errorMsg,
                };
                return { ...prev, parts: newParts };
              });
              reject(error instanceof Error ? error : new Error(errorMsg));
            });

            uppy.on('restriction-failed', (_uploadedFile, error) => {
              reject(error instanceof Error ? error : new Error(String(error)));
            });

            uppy.on('cancel-all', () => {
              reject(new Error('Загрузка отменена'));
            });

            void uppy.upload();
          }),
      );

      const results = await Promise.all(uploadPromises);

      // Confirm completion for all parts
      try {
        await Promise.all(
          sessions.map((session, index) => {
            const result = results[index];
            if (!result) {
              return Promise.resolve();
            }
            return confirmUploadComplete(transcriptionId!, {
              uploadSessionId: session.uploadSessionId,
              ...(result.uploadUrl ? { tusUploadUrl: result.uploadUrl } : {}),
            });
          }),
        );
      } catch {
        // post-finish hook мог уже подтвердить загрузку — продолжаем переход.
      }

      destroyUppies();
      void navigate(`/transcriptions/${encodeURIComponent(transcriptionId)}`);
    } catch (error) {
      const message = describeError(error);
      if (message !== 'Загрузка отменена') {
        setSubmitError(message);
      }
      setUploading(false);
      setPaused(false);
    }
  });

  const onPauseResume = () => {
    if (uppiesRef.current.length === 0) {
      return;
    }
    if (paused) {
      uppiesRef.current.forEach((uppy) => uppy.resumeAll());
      setPaused(false);
      return;
    }
    uppiesRef.current.forEach((uppy) => uppy.pauseAll());
    setPaused(true);
  };

  const onCancelUpload = () => {
    uppiesRef.current.forEach((uppy) => uppy?.cancelAll());
    destroyUppies();
    setUploading(false);
    setPaused(false);
    setProgress(null);
    setSubmitError(null);
  };

  const titleRegister = register('title', {
    onChange: () => {
      titleEditedRef.current = true;
    },
  });

  const canSubmit = files.length > 0 && fileError === null && !uploading;
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const isMultipart = files.length > 1;

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Журнал встреч', to: '/transcriptions' },
          { label: 'Добавить встречу' },
        ]}
        title="Добавить встречу"
        description="Укажите сведения о встрече и загрузите запись."
        actions={
          <ButtonLink variant="secondary" to="/transcriptions">
            К журналу
          </ButtonLink>
        }
      />

      <div className="stack">
        <Surface title="Файл">
          <UploadDropzone
            files={files}
            disabled={uploading}
            error={fileError}
            onFilesChange={onFilesChange}
          />
        </Surface>

        <Surface title="Сведения о встрече">
          <form className="form" onSubmit={runUpload} noValidate>
            <div className="form-grid">
              <Field
                htmlFor="title"
                label="Название встречи"
                {...(errors.title?.message !== undefined ? { error: errors.title.message } : {})}
              >
                <input
                  id="title"
                  className="field__control"
                  type="text"
                  autoComplete="off"
                  disabled={uploading}
                  aria-invalid={errors.title !== undefined}
                  {...titleRegister}
                />
              </Field>

              <Field htmlFor="meetingType" label="Тип встречи">
                <select
                  id="meetingType"
                  className="field__control"
                  disabled={uploading}
                  {...register('meetingType')}
                >
                  {MEETING_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                htmlFor="meetingDate"
                label="Дата встречи"
                {...(errors.meetingDate?.message !== undefined
                  ? { error: errors.meetingDate.message }
                  : {})}
              >
                <MeetingDatePicker
                  id="meetingDate"
                  value={watch('meetingDate')}
                  disabled={uploading}
                  invalid={errors.meetingDate?.message !== undefined}
                  onChange={(next) =>
                    setValue('meetingDate', next, { shouldDirty: true, shouldValidate: true })
                  }
                />
              </Field>

              {showClientFields ? (
                <Field
                  htmlFor="clientName"
                  label="Клиент"
                  hint="Формат: юр. наименование, форма собственности. Примеры: «Зубко Л.В., ИП»; «Рыбинск, ООО (Завод Металлоконструкций)». Можно выбрать из уже встречавшихся или ввести новое."
                  {...(errors.clientName?.message !== undefined
                    ? { error: errors.clientName.message }
                    : {})}
                >
                  <ClientNameCombobox
                    id="clientName"
                    value={clientName}
                    disabled={uploading}
                    suggestions={clientSuggestionsQuery.data ?? []}
                    invalid={errors.clientName?.message !== undefined}
                    onChange={(next) =>
                      setValue('clientName', next, { shouldDirty: true, shouldValidate: true })
                    }
                  />
                </Field>
              ) : null}
            </div>

            <Field htmlFor="axium-search" label="Участники AXIUM">
              <input
                id="axium-search"
                className="field__control"
                type="search"
                placeholder="Поиск сотрудника"
                value={axiumSearch}
                disabled={uploading}
                onChange={(event) => setAxiumSearch(event.target.value)}
              />
              {filteredUsers.length > 0 ? (
                <ul className="picker-list" aria-label="Доступные сотрудники">
                  {filteredUsers.slice(0, 8).map((user) => (
                    <li key={user.id}>
                      <button
                        type="button"
                        className="picker-list__item"
                        disabled={uploading}
                        onClick={() => {
                          setAxiumParticipantIds((prev) => [...prev, user.id]);
                          setAxiumSearch('');
                        }}
                      >
                        {user.displayName}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <ParticipantChips
                ariaLabel="Выбранные участники AXIUM"
                items={selectedAxium.map((user) => ({ id: user.id, label: user.displayName }))}
                onRemove={(id) => {
                  setAxiumParticipantIds((prev) => prev.filter((item) => item !== id));
                }}
              />
            </Field>

            {showClientFields ? (
              <Field
                htmlFor="client-participant"
                label="Участники клиента"
                hint="Введите имя и нажмите Enter."
              >
                <input
                  id="client-participant"
                  className="field__control"
                  type="text"
                  value={clientParticipantDraft}
                  disabled={uploading}
                  onChange={(event) => setClientParticipantDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addClientParticipant();
                    }
                  }}
                />
                <ParticipantChips
                  ariaLabel="Участники клиента"
                  items={clientParticipants.map((name) => ({ id: name, label: name }))}
                  onRemove={(id) => {
                    setClientParticipants((prev) => prev.filter((item) => item !== id));
                  }}
                />
              </Field>
            ) : null}

            <p className="form-meta">
              Загрузит: <strong>{meQuery.data?.name ?? 'текущий пользователь'}</strong>
            </p>

            {progress !== null ? (
              <div className="upload-progress" role="status" aria-live="polite">
                {isMultipart ? (
                  <>
                    <div className="upload-progress__header">
                      <span className="upload-progress__percent">
                        {Math.round(
                          (progress.parts.reduce((sum, p) => sum + p.bytesUploaded, 0) /
                            totalSize) *
                            100,
                        )}
                        %
                      </span>
                      <span className="upload-progress__meta">
                        {progress.parts.filter((p) => p.status === 'completed').length} /{' '}
                        {progress.parts.length} частей ·{' '}
                        {formatFileSize(
                          String(progress.parts.reduce((sum, p) => sum + p.bytesUploaded, 0)),
                        )}{' '}
                        из {formatFileSize(String(totalSize))} · {formatSpeed(progress.bytesPerSecond)}
                      </span>
                    </div>
                    <ul className="upload-progress__parts">
                      {progress.parts.map((part, idx) => (
                        <li key={idx} className="upload-progress__part">
                          <div className="upload-progress__part-header">
                            <span className="upload-progress__part-name">
                              #{idx + 1} {part.fileName}
                            </span>
                            <span className="upload-progress__part-status">
                              {part.status === 'completed'
                                ? '✓'
                                : part.status === 'error'
                                  ? '✗'
                                  : `${part.percentage}%`}
                            </span>
                          </div>
                          {part.status === 'error' ? (
                            <div className="upload-progress__part-error">{part.error}</div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <>
                    <div className="upload-progress__header">
                      <span className="upload-progress__percent">
                        {progress.parts[0]?.percentage ?? 0}%
                      </span>
                      <span className="upload-progress__meta">
                        {formatFileSize(String(progress.parts[0]?.bytesUploaded ?? 0))} из{' '}
                        {formatFileSize(String(progress.parts[0]?.bytesTotal ?? 0))} ·{' '}
                        {formatSpeed(progress.bytesPerSecond)} ·{' '}
                        {formatEta(
                          progress.parts[0]?.bytesUploaded ?? 0,
                          progress.parts[0]?.bytesTotal ?? 0,
                          progress.bytesPerSecond,
                        )}
                      </span>
                    </div>
                    <div className="upload-progress__track" aria-hidden="true">
                      <div
                        className="upload-progress__bar"
                        style={{ width: `${progress.parts[0]?.percentage ?? 0}%` }}
                      />
                    </div>
                  </>
                )}
                <div className="page-header__actions">
                  <Button type="button" variant="secondary" onClick={onPauseResume}>
                    {paused ? 'Продолжить' : 'Пауза'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={onCancelUpload}>
                    Отменить
                  </Button>
                </div>
              </div>
            ) : null}

            {submitError !== null ? (
              <div className="notice notice--danger" role="alert">
                <div>
                  <p className="notice__title">Не удалось загрузить файл</p>
                  <p className="notice__text">{submitError}</p>
                </div>
              </div>
            ) : null}

            <div className="page-header__actions">
              <Button type="submit" disabled={!canSubmit}>
                {uploading ? 'Загрузка…' : 'Загрузить и обработать'}
              </Button>
            </div>
          </form>
        </Surface>
      </div>
    </>
  );
}
